import { addYears } from "../../../../../../react-core/utils/date";
import { TransferType } from "../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { PensionTransfer } from "../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { PensionBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { MoneyHelper } from "../../../../../Shared/Common/MoneyHelper";
import { TransferHelper } from "./TransferHelper";

export class PensionHelper {
    static clientSinglePayment(model: PensionBase): Money {
        let taxReliefValue: number =
            model && model?.TaxReliefPercentage && model?.TaxReliefPercentage?.Value
                ? 1 + model?.TaxReliefPercentage?.Value
                : 1.25;
        if (!model?.ClientSinglePayment?.TaxRelief) {
            taxReliefValue = 1;
        }
        let result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.ClientSinglePayment != null && model?.ClientSinglePayment.Money != null) {
            result = {
                Amount: model.ClientSinglePayment.Money.Amount * taxReliefValue,
                Currency: model.ClientSinglePayment.Money.Currency,
            };
        }

        return result;
    }

    static employerSinglePayment(model: PensionBase): Money {
        let result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.EmployerSinglePayment != null && model?.EmployerSinglePayment?.Money != null)
            {result = model.EmployerSinglePayment.Money;}
        return result;
    }

    static thirdPartySinglePayment(model: PensionBase): Money {
        const taxReliefValue: number =
            model && model.TaxReliefPercentage && model.TaxReliefPercentage.Value
                ? 1 + model.TaxReliefPercentage.Value
                : 1.25;
        const result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.ThirdPartySinglePayment != null && model?.ThirdPartySinglePayment?.Money != null) {
            const isTaxReliefApplicable = model.ThirdPartySinglePayment.TaxRelief;
            result.Amount = isTaxReliefApplicable
                ? MoneyHelper.roundNumberToDecimalPlaces(model.ThirdPartySinglePayment.Money.Amount * taxReliefValue, 2)
                : model.ThirdPartySinglePayment.Money.Amount;
        }

        return result;
    }

    static clientRegularPayment(model: PensionBase): Money {
        const taxReliefValue: number =
            model && model.TaxReliefPercentage && model.TaxReliefPercentage.Value
                ? 1 + model.TaxReliefPercentage.Value
                : 1.25;
        let result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.ClientRegularPayment != null && model?.ClientRegularPayment.Money != null) {
            result = {
                Amount: model.ClientRegularPayment.Money.Amount * taxReliefValue,
                Currency: model.ClientRegularPayment.Money.Currency,
            };
        }

        return result;
    }

    static employerRegularPayment(model: PensionBase): Money {
        let result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.EmployerRegularPayment != null && model?.EmployerRegularPayment?.Money != null)
            {result = model.EmployerRegularPayment.Money;}
        return result;
    }

    static thirdPartyRegulaPayment(model: PensionBase): Money {
        const taxReliefValue: number =
            model && model.TaxReliefPercentage && model.TaxReliefPercentage.Value
                ? 1 + model.TaxReliefPercentage.Value
                : 1.25;
        const result: Money = {
            Amount: 0,
            Currency: MoneyHelper.getDefaultCurrency(),
        };
        if (model?.ThirdPartyRegularPayment != null && model?.ThirdPartyRegularPayment.Money != null) {
            const isTaxReliefApplicable = model.ThirdPartyRegularPayment.TaxRelief;
            result.Amount = isTaxReliefApplicable
                ? MoneyHelper.roundNumberToDecimalPlaces(
                      model.ThirdPartyRegularPayment.Money.Amount * taxReliefValue,
                      2
                  )
                : model.ThirdPartyRegularPayment.Money.Amount;
        }

        return result;
    }

    static singlePaymentsTotal(model: PensionBase): number {
        const client = this.clientSinglePayment(model);
        const employer = this.employerSinglePayment(model);
        const thirdparty = this.thirdPartySinglePayment(model);
        return client.Amount + employer.Amount + thirdparty.Amount;
    }

    static regularPaymentsTotalForCharges(model: PensionBase): number {
        const client = this.clientRegularPayment(model);
        const employer = this.employerRegularPayment(model);
        const thirdparty = this.thirdPartyRegulaPayment(model);
        return client.Amount + employer.Amount + thirdparty.Amount;
    }

    static getTransfersTotal(transfers: PensionTransfer[]) {
        let result = 0;

        if (!transfers || transfers.length === 0) {return result;}

        transfers.forEach((transfer) => {
            if (
                transfer.TransferType === TransferType.Cash &&
                transfer.CurrentValue &&
                transfer.CurrentValue.Amount > 0
            ) {
                result += transfer.CurrentValue.Amount;
            } else {
                result += TransferHelper.getTransferInvestmentTotal(transfer.Instruments, transfer.ReRegisterValue);
            }
        });

        return result;
    }

    static transfersTotal(model: PensionBase): number {
        const transfers = model?.PensionTransfers;
        return this.getTransfersTotal(transfers);
    }

    static hasRegularPayments(model: PensionBase): boolean {
        if (model == null) {return false;}

        const client = model.ClientRegularPayment != null && model.ClientRegularPayment.Money != null;
        const employer = model.EmployerRegularPayment != null && model.EmployerRegularPayment.Money != null;
        const thirdParty = model.ThirdPartyRegularPayment != null && model.ThirdPartyRegularPayment.Money != null;

        return client || employer || thirdParty;
    }

    static hasSinglePayments(model: PensionBase): boolean {
        if (model == null) {return false;}

        const client = model.ClientSinglePayment != null && model.ClientSinglePayment.Money != null;
        const employer = model.EmployerSinglePayment != null && model.EmployerSinglePayment.Money != null;
        const thirdParty = model.ThirdPartySinglePayment != null && model.ThirdPartySinglePayment.Money != null;

        return client || employer || thirdParty;
    }

    static hasPensionTransfers(model: PensionBase): boolean {
        return model && model.PensionTransfers && model.PensionTransfers.length > 0;
    }
}

export const LessThanAge = (age: number, dateOfBirth: Date) => {
    const now = new Date();
    return addYears(dateOfBirth, age) > now;
};
